import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Users, Eye, Globe, TrendingUp, Activity, Layers } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid,
} from 'recharts';

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="glass-cyber rounded-2xl p-5" data-testid={`stat-${label.toLowerCase().replace(/ /g,'-')}`}>
      <div className="flex items-center justify-between mb-3">
        <Icon className="text-[#00FFD1]" size={20} />
        <div className="font-mono-cyber text-[10px] text-[#00FFD1] tracking-widest">// {label.toUpperCase()}</div>
      </div>
      <div className="font-orbitron text-3xl text-white">{value}</div>
      {sub && <div className="font-mono-cyber text-xs text-neutral-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const { API, user, authChecked } = useApp();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!authChecked) return;
    if (!user) { nav('/auth'); return; }
    if (!user.is_admin) { setErr('Admin only.'); return; }
    const load = () => axios.get(`${API}/admin/stats`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('pp-token') || ''}` },
    }).then(r => setData(r.data)).catch(e => setErr(e.response?.data?.detail || e.message));
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [user, authChecked]);

  if (err) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="glass-cyber rounded-2xl p-8 max-w-md text-center">
          <div className="font-orbitron text-2xl text-[#00FFD1] mb-3">// ACCESS DENIED</div>
          <div className="font-mono-cyber text-sm text-neutral-300">{err}</div>
          <button data-testid="admin-back" onClick={() => nav('/')} className="mt-6 px-5 py-2 bg-[#00FFD1] text-black font-orbitron rounded-lg">Back</button>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-black text-[#00FFD1] font-mono-cyber flex items-center justify-center">// loading dashboard…</div>;

  const t = data.totals;
  return (
    <div className="min-h-screen bg-black text-white px-6 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="font-mono-cyber text-xs text-[#00FFD1]">// ADMIN CONSOLE // LIVE</div>
            <h1 className="font-orbitron text-4xl">PrepPulse Ops</h1>
          </div>
          <button data-testid="admin-refresh" onClick={() => nav('/')} className="font-mono-cyber text-[#00FFD1]">
            ← back
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Activity} label="Active Users" value={data.active.users_last_5min}
            sub={`${data.active.sessions_last_5min} sessions · last 5 min`} />
          <StatCard icon={Users} label="Total Users" value={t.users}
            sub={`${data.demographics.providers.map(p => `${p.provider}:${p.users}`).join(' · ')}`} />
          <StatCard icon={Eye} label="Total Pageviews" value={t.pageviews}
            sub={`24h: ${t.pageviews_24h} · 7d: ${t.pageviews_7d}`} />
          <StatCard icon={TrendingUp} label="Rebirths" value={t.total_rebirths}
            sub={`E:${t.tower_progress_english} M:${t.tower_progress_math} S:${t.tower_progress_science}`} />
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="glass-cyber rounded-2xl p-5" data-testid="chart-signups">
            <div className="font-mono-cyber text-xs text-[#00FFD1] mb-3">// SIGNUPS — LAST 7 DAYS</div>
            <div style={{ width: '100%', height: 200 }}>
              <ResponsiveContainer>
                <LineChart data={data.signups_last_7d}>
                  <CartesianGrid stroke="#00FFD120" />
                  <XAxis dataKey="day" tick={{ fill: '#00FFD1', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#00FFD1', fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: '#000', border: '1px solid #00FFD1', fontSize: 12, color: '#00FFD1' }} />
                  <Line type="monotone" dataKey="signups" stroke="#00FFD1" strokeWidth={2} dot={{ fill: '#00FFD1' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="glass-cyber rounded-2xl p-5" data-testid="chart-top-pages">
            <div className="font-mono-cyber text-xs text-[#00FFD1] mb-3">// TOP PAGES — LAST 7 DAYS</div>
            <div style={{ width: '100%', height: 200 }}>
              <ResponsiveContainer>
                <BarChart data={data.top_pages_7d} layout="vertical">
                  <CartesianGrid stroke="#00FFD120" />
                  <XAxis type="number" tick={{ fill: '#00FFD1', fontSize: 10 }} />
                  <YAxis dataKey="path" type="category" tick={{ fill: '#00FFD1', fontSize: 10 }} width={120} />
                  <Tooltip contentStyle={{ background: '#000', border: '1px solid #00FFD1', fontSize: 12, color: '#00FFD1' }} />
                  <Bar dataKey="views" fill="#00FFD1" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="glass-cyber rounded-2xl p-5" data-testid="demo-providers">
            <div className="font-mono-cyber text-xs text-[#00FFD1] mb-3">// USER PROVIDERS</div>
            {data.demographics.providers.map(p => (
              <div key={p.provider} className="flex items-center justify-between py-1 font-mono-cyber text-sm">
                <span className="text-neutral-300">{p.provider}</span>
                <span className="text-[#00FFD1]">{p.users}</span>
              </div>
            ))}
          </div>
          <div className="glass-cyber rounded-2xl p-5" data-testid="demo-countries">
            <div className="font-mono-cyber text-xs text-[#00FFD1] mb-3">
              <Globe size={12} className="inline mr-1" /> // TOP COUNTRIES BY VIEWS
            </div>
            {data.demographics.top_countries.length === 0 ? (
              <div className="font-mono-cyber text-xs text-neutral-500">// no country data collected yet</div>
            ) : data.demographics.top_countries.map(c => (
              <div key={c.country} className="flex items-center justify-between py-1 font-mono-cyber text-sm">
                <span className="text-neutral-300">{c.country}</span>
                <span className="text-[#00FFD1]">{c.views}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Layers} label="Daily Today" value={t.daily_completions_today} />
          <StatCard icon={Layers} label="English Tower" value={t.tower_progress_english} />
          <StatCard icon={Layers} label="Math Tower" value={t.tower_progress_math} />
          <StatCard icon={Layers} label="Science Tower" value={t.tower_progress_science} />
        </div>

        <div className="mt-4 font-mono-cyber text-[10px] text-neutral-500">
          // generated_at {data.generated_at} · auto-refresh 5s
        </div>
      </div>
    </div>
  );
}
